import React, { useEffect } from "react";
import { EditOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input } from "antd";
import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../lib/UploadFoodImage";

interface AboutTaxProps {
  isEditing: boolean;
  setIsEditing: (value: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  form: any;
  aboutTaxData?: { heading?: string; description?: string; image?: string };
  loading?: boolean;
}

const AboutTax: React.FC<AboutTaxProps> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  aboutTaxData,
  loading,
}) => {
  useEffect(() => {
    if (aboutTaxData) {
      form.setFieldsValue({
        aboutTax: {
          heading: aboutTaxData.heading || "",
          description: aboutTaxData.description || "",
          image: aboutTaxData.image ? [aboutTaxData.image] : [],
        },
      });
    } else {
      form.resetFields();
    }
  }, [aboutTaxData, form]);

  return (
    <div>
      {!isEditing ? (
        <>
          {/* Desktop Table */}
          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full border-collapse">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Image
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Heading
                  </th>
                  <th className="py-3 px-4 text-center text-sm md:text-base">
                    Description
                  </th>
                  <th className="py-3 px-4 text-end text-sm md:text-base">
                    Action
                  </th>
                </tr>
              </thead>

              <tbody>
                {aboutTaxData ? (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td className="py-3 px-4 w-[120px]">
                      <Image
                        src={aboutTaxData?.image || "/placeholder.jpg"}
                        height={50}
                        width={120}
                        className="object-contain w-full h-full"
                        alt="About Tax"
                      />
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {aboutTaxData?.heading || "—"}
                    </td>

                    <td className="py-3 px-4 text-center text-gray-700">
                      {aboutTaxData?.description || "—"}
                    </td>

                    <td className="py-3 px-4 text-primary">
                      <div className="flex items-center justify-end gap-4">
                        <EditOutlined
                          onClick={() => setIsEditing(true)}
                          className="cursor-pointer hover:text-blue-600 text-[20px]"
                        />
                      </div>
                    </td>
                  </tr>
                ) : (
                  <tr className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]">
                    <td colSpan={4} className="text-center py-6 text-gray-500">
                      <Button type="dashed" onClick={() => setIsEditing(true)}>
                        Add data
                      </Button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile View */}
          <div className="sm:hidden">
            {aboutTaxData ? (
              <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-3">
                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Image:
                  </span>
                  <Image
                    src={aboutTaxData?.image || "/placeholder.jpg"}
                    height={70}
                    width={70}
                    className="object-contain w-full h-full"
                    alt="About Tax"
                  />
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Heading:
                  </span>
                  <p>{aboutTaxData?.heading || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Description:
                  </span>
                  <p>{aboutTaxData?.description || "—"}</p>
                </div>

                <div>
                  <span className="font-semibold text-primary block mb-1">
                    Action:
                  </span>
                  <EditOutlined
                    onClick={() => setIsEditing(true)}
                    className="cursor-pointer text-black text-[18px]"
                  />
                </div>
              </div>
            ) : (
              <div className="bg-[#F9F9F9] border flex justify-center border-gray-200 rounded-lg p-4 shadow-sm">
                <Button type="dashed" onClick={() => setIsEditing(true)}>
                  Add Data
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        /* Edit Mode */
        <div>
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-lg sm:rounded-2xl shadow-sm">
            <Form layout="vertical" form={form}>
              <Form.Item
                label="Heading"
                name={["aboutTax", "heading"]}
                rules={[{ required: true, message: "Please enter heading" }]}
              >
                <Input placeholder="Enter heading" />
              </Form.Item>

              <Form.Item
                label="Description"
                name={["aboutTax", "description"]}
                rules={[
                  { required: true, message: "Please enter description" },
                ]}
              >
                <Input placeholder="Enter description" />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["aboutTax", "image"]}
                rules={[{ required: true, message: "Please upload image" }]}
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>
            </Form>
          </div>

          <div className="flex justify-start gap-3 mt-6 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>

            <ButtonWrapper onClick={handleCancel}>
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutTax;